import axios from 'axios';
import { HolidayInstance, HolidayRule } from '@timesheet/schemas';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export interface CompanyClosingImportPreviewRow {
  rowNumber: number;
  valid: boolean;
  error?: string;
  name?: string;
  date?: string;
  endDate?: string;
}

export interface CompanyClosingImportResult {
  total: number;
  created: number;
  valid: number;
  errors: string[];
  rows: CompanyClosingImportPreviewRow[];
}

export const nationalHolidayService = {
  getAll: async (year: number): Promise<HolidayInstance[]> => {
    const response = await axios.get(`${API_URL}/holidays/instances?year=${year}`, getAuthHeaders());
    return response.data;
  },

  getRules: async (): Promise<HolidayRule[]> => {
    const response = await axios.get(`${API_URL}/holidays/rules`, getAuthHeaders());
    return response.data;
  },

  createRule: async (data: { name: string; type: 'fixed' | 'floating' | 'single'; rule: Record<string, unknown>; allowTimesheetEntry?: boolean }): Promise<HolidayRule> => {
    const response = await axios.post(`${API_URL}/holidays/rules`, data, getAuthHeaders());
    return response.data;
  },

  updateRule: async (id: number, data: { name?: string; active?: boolean; rule?: Record<string, unknown>; allowTimesheetEntry?: boolean }): Promise<HolidayRule> => {
    const response = await axios.patch(`${API_URL}/holidays/rules/${id}`, data, getAuthHeaders());
    return response.data;
  },

  deleteRule: async (id: number): Promise<void> => {
    await axios.delete(`${API_URL}/holidays/rules/${id}`, getAuthHeaders());
  },

  generate: async (year: number): Promise<HolidayInstance[]> => {
    const response = await axios.post(`${API_URL}/holidays/generate?year=${year}`, undefined, getAuthHeaders());
    return response.data;
  },

  updateInstance: async (id: number, data: { name?: string; active?: boolean; isCompanyClosing?: boolean; date?: string; endDate?: string }): Promise<HolidayInstance> => {
    const response = await axios.patch(`${API_URL}/holidays/instances/${id}`, data, getAuthHeaders());
    return response.data;
  },

  createInstance: async (data: { name: string; date: string; endDate?: string; isCompanyClosing?: boolean; tenantId?: number }): Promise<HolidayInstance> => {
    const response = await axios.post(`${API_URL}/holidays/instances`, data, getAuthHeaders());
    return response.data;
  },

  deleteInstance: async (id: number): Promise<void> => {
    await axios.delete(`${API_URL}/holidays/instances/${id}`, getAuthHeaders());
  },

  previewCompanyClosingsImport: async (file: File): Promise<CompanyClosingImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/holidays/company-closings/import/preview`,
      formData,
      {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return response.data;
  },

  importCompanyClosingsFromCsv: async (file: File): Promise<CompanyClosingImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/holidays/company-closings/import`,
      formData,
      {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return response.data;
  },

  downloadCompanyClosingsImportTemplate: async (): Promise<Blob> => {
    const response = await axios.get(
      `${API_URL}/holidays/company-closings/import/template`,
      {
        ...getAuthHeaders(),
        responseType: 'arraybuffer',
      },
    );

    return new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  },
};
